import { createSlice } from "@reduxjs/toolkit";
import {
  createMedicalCase,
  fetchMyCases,
  fetchPublicCases,
  fetchCaseById,
  updateMedicalCase,
  deleteMedicalCase,
  addAttachments,
  fetchAttachment,
  deleteAttachment,
  createAnnotation,
  fetchAnnotations,
  updateAnnotation,
  deleteAnnotation,
  toggleCaseLike,
  fetchCaseLikes,
  createCaseComment,
  fetchCaseComments,
  updateCaseComment,
  deleteCaseComment,
  createLive,
  fetchMyLives,
  fetchPublicLives,
  fetchLiveById,
  updateLive,
  startLive,
  joinLive,
  fetchLiveParticipants,
  leaveLive,
  endLive,
  cancelLive,
  createLiveComment,
  fetchLiveComments,
  updateLiveComment,
  deleteLiveComment,
} from "../thunks/medicalCasesThunks";

// Annotations are keyed per (caseId, attachmentId) since a case can have
// several attachments, each with its own set of annotations.
const annotationKey = (caseId, attachmentId) => `${caseId}:${attachmentId}`;

const normalizeList = (payload) =>
  Array.isArray(payload) ? payload : payload?.items || [];

const upsertById = (list, item) => {
  const id = item?.id || item?._id;
  const idx = list.findIndex((i) => (i.id || i._id) === id);
  if (idx === -1) return [...list, item];
  const next = [...list];
  next[idx] = item;
  return next;
};

const initialState = {
  // Cases
  casesList: {
    my: { items: [], total: 0, status: "idle", error: null },
    public: { items: [], total: 0, status: "idle", error: null },
  },
  currentCase: { data: null, status: "idle", error: null },
  casesById: {}, // Normalised by ID

  // Attachments
  attachments: {},
  attachmentStatus: "idle",
  attachmentError: null,

  // Annotations — keyed by "caseId:attachmentId"
  annotations: {},
  annotationsStatus: "idle",
  annotationsError: null,

  // Likes
  likesById: {},
  likesStatus: "idle",
  likesError: null,

  // Comments
  commentsByCaseId: {},
  commentsStatus: "idle",
  commentsError: null,

  // Lives
  livesList: {
    my: { items: [], total: 0, status: "idle", error: null },
    public: { items: [], total: 0, status: "idle", error: null },
  },
  currentLive: { data: null, status: "idle", error: null },
  livesById: {}, // Normalised by ID — separate from casesById
  liveConnection: {
    liveId: null,
    serverUrl: null,
    participantToken: null,
    participant: null,
    status: "idle",
    error: null,
  },
  liveParticipantsById: {},
  liveParticipantsStatusById: {},
  liveParticipantsErrorById: {},
  liveCommentsById: {},
  liveCommentsStatus: "idle",
  liveCommentsError: null,

  // General
  error: null,
  successMessage: null,
};

const medicalCasesSlice = createSlice({
  name: "medicalCases",
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
      state.attachmentError = null;
      state.annotationsError = null;
      state.likesError = null;
      state.commentsError = null;
      state.liveCommentsError = null;
    },
    clearSuccessMessage(state) {
      state.successMessage = null;
    },
    setLiveConnectionStatus(state, action) {
      state.liveConnection.status = action.payload;
      state.liveConnection.error = null;
    },
    clearLiveConnection(state) {
      state.liveConnection = { ...initialState.liveConnection };
    },
    resetState() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    // ========== CASES ==========

    // fetchMyCases
    builder
      .addCase(fetchMyCases.pending, (state) => {
        state.casesList.my.status = "loading";
        state.casesList.my.error = null;
      })
      .addCase(fetchMyCases.fulfilled, (state, action) => {
        const data = normalizeList(action.payload);
        state.casesList.my.items = data;
        state.casesList.my.total = action.payload?.total || data.length;
        state.casesList.my.status = "succeeded";

        data.forEach((c) => {
          const id = c.id || c._id;
          if (id) state.casesById[id] = c;
        });
      })
      .addCase(fetchMyCases.rejected, (state, action) => {
        state.casesList.my.status = "failed";
        state.casesList.my.error = action.payload;
      });

    // fetchPublicCases
    builder
      .addCase(fetchPublicCases.pending, (state) => {
        state.casesList.public.status = "loading";
        state.casesList.public.error = null;
      })
      .addCase(fetchPublicCases.fulfilled, (state, action) => {
        const data = normalizeList(action.payload);
        state.casesList.public.items = data;
        state.casesList.public.total = action.payload?.total || data.length;
        state.casesList.public.status = "succeeded";

        data.forEach((c) => {
          const id = c.id || c._id;
          if (id) state.casesById[id] = c;
        });
      })
      .addCase(fetchPublicCases.rejected, (state, action) => {
        state.casesList.public.status = "failed";
        state.casesList.public.error = action.payload;
      });

    // fetchCaseById
    builder
      .addCase(fetchCaseById.pending, (state) => {
        state.currentCase.status = "loading";
        state.currentCase.error = null;
      })
      .addCase(fetchCaseById.fulfilled, (state, action) => {
        state.currentCase.data = action.payload;
        state.currentCase.status = "succeeded";
        const id = action.payload?.id || action.payload?._id;
        if (id) state.casesById[id] = action.payload;
      })
      .addCase(fetchCaseById.rejected, (state, action) => {
        state.currentCase.status = "failed";
        state.currentCase.error = action.payload;
      });

    // createMedicalCase
    builder
      .addCase(createMedicalCase.pending, (state) => {
        state.currentCase.status = "loading";
        state.error = null;
      })
      .addCase(createMedicalCase.fulfilled, (state, action) => {
        state.currentCase.data = action.payload;
        state.currentCase.status = "succeeded";
        const id = action.payload?.id || action.payload?._id;
        if (id) state.casesById[id] = action.payload;
        state.successMessage = "Case created successfully";
      })
      .addCase(createMedicalCase.rejected, (state, action) => {
        state.currentCase.status = "failed";
        state.error = action.payload;
      });

    // updateMedicalCase
    builder
      .addCase(updateMedicalCase.pending, (state) => {
        state.currentCase.status = "loading";
        state.error = null;
      })
      .addCase(updateMedicalCase.fulfilled, (state, action) => {
        state.currentCase.data = action.payload;
        state.currentCase.status = "succeeded";
        const id = action.payload?.id || action.payload?._id;
        if (id) state.casesById[id] = action.payload;
        state.successMessage = "Case updated successfully";
      })
      .addCase(updateMedicalCase.rejected, (state, action) => {
        state.currentCase.status = "failed";
        state.error = action.payload;
      });

    // deleteMedicalCase
    builder
      .addCase(deleteMedicalCase.pending, (state) => {
        state.currentCase.status = "loading";
        state.error = null;
      })
      .addCase(deleteMedicalCase.fulfilled, (state, action) => {
        const id = action.payload;
        delete state.casesById[id];
        state.casesList.my.items = state.casesList.my.items.filter(
          (c) => (c.id || c._id) !== id
        );
        state.casesList.public.items = state.casesList.public.items.filter(
          (c) => (c.id || c._id) !== id
        );
        state.currentCase.data = null;
        state.currentCase.status = "idle";
        state.successMessage = "Case deleted successfully";
      })
      .addCase(deleteMedicalCase.rejected, (state, action) => {
        state.currentCase.status = "failed";
        state.error = action.payload;
      });

    // ========== ATTACHMENTS ==========

    builder
      .addCase(addAttachments.pending, (state) => {
        state.attachmentStatus = "loading";
        state.attachmentError = null;
      })
      .addCase(addAttachments.fulfilled, (state, action) => {
        state.attachmentStatus = "succeeded";
        state.successMessage = "Attachments added successfully";

        // Keep currentCase.data in sync so the UI doesn't need a full refetch
        const { caseId, attachments } = action.payload || {};
        const newAttachments = normalizeList(attachments);
        if (caseId && state.currentCase.data) {
          const currentId =
            state.currentCase.data.id || state.currentCase.data._id;
          if (currentId === caseId) {
            const existing = state.currentCase.data.attachments || [];
            state.currentCase.data.attachments = [
              ...existing,
              ...newAttachments,
            ];
          }
        }
        if (caseId && state.casesById[caseId]) {
          const existing = state.casesById[caseId].attachments || [];
          state.casesById[caseId].attachments = [
            ...existing,
            ...newAttachments,
          ];
        }
      })
      .addCase(addAttachments.rejected, (state, action) => {
        state.attachmentStatus = "failed";
        state.attachmentError = action.payload;
      });

    builder
      .addCase(deleteAttachment.pending, (state) => {
        state.attachmentStatus = "loading";
        state.attachmentError = null;
      })
      .addCase(deleteAttachment.fulfilled, (state, action) => {
        state.attachmentStatus = "succeeded";
        state.successMessage = "Attachment deleted";

        const { caseId, attachmentId } = action.payload || {};
        const strip = (attachments = []) =>
          attachments.filter(
            (a) => (a.id || a._id) !== attachmentId
          );

        if (
          caseId &&
          state.currentCase.data &&
          (state.currentCase.data.id || state.currentCase.data._id) === caseId
        ) {
          state.currentCase.data.attachments = strip(
            state.currentCase.data.attachments
          );
        }
        if (caseId && state.casesById[caseId]) {
          state.casesById[caseId].attachments = strip(
            state.casesById[caseId].attachments
          );
        }
      })
      .addCase(deleteAttachment.rejected, (state, action) => {
        state.attachmentStatus = "failed";
        state.attachmentError = action.payload;
      });

    // ========== ANNOTATIONS ==========

    builder
      .addCase(fetchAnnotations.pending, (state) => {
        state.annotationsStatus = "loading";
        state.annotationsError = null;
      })
      .addCase(fetchAnnotations.fulfilled, (state, action) => {
        const { caseId, attachmentId } = action.meta.arg || {};
        if (caseId && attachmentId) {
          state.annotations[annotationKey(caseId, attachmentId)] =
            normalizeList(action.payload);
        }
        state.annotationsStatus = "succeeded";
      })
      .addCase(fetchAnnotations.rejected, (state, action) => {
        state.annotationsStatus = "failed";
        state.annotationsError = action.payload;
      });

    builder
      .addCase(createAnnotation.pending, (state) => {
        state.annotationsStatus = "loading";
        state.annotationsError = null;
      })
      .addCase(createAnnotation.fulfilled, (state, action) => {
        const { caseId, attachmentId } = action.meta.arg || {};
        if (caseId && attachmentId) {
          const key = annotationKey(caseId, attachmentId);
          const existing = state.annotations[key] || [];
          state.annotations[key] = [...existing, action.payload];
        }
        state.annotationsStatus = "succeeded";
        state.successMessage = "Annotation created";
      })
      .addCase(createAnnotation.rejected, (state, action) => {
        state.annotationsStatus = "failed";
        state.annotationsError = action.payload;
      });

    builder
      .addCase(updateAnnotation.pending, (state) => {
        state.annotationsStatus = "loading";
        state.annotationsError = null;
      })
      .addCase(updateAnnotation.fulfilled, (state, action) => {
        const { caseId, attachmentId } = action.meta.arg || {};
        if (caseId && attachmentId) {
          const key = annotationKey(caseId, attachmentId);
          state.annotations[key] = upsertById(
            state.annotations[key] || [],
            action.payload
          );
        }
        state.annotationsStatus = "succeeded";
        state.successMessage = "Annotation updated";
      })
      .addCase(updateAnnotation.rejected, (state, action) => {
        state.annotationsStatus = "failed";
        state.annotationsError = action.payload;
      });

    builder
      .addCase(deleteAnnotation.pending, (state) => {
        state.annotationsStatus = "loading";
        state.annotationsError = null;
      })
      .addCase(deleteAnnotation.fulfilled, (state, action) => {
        const { caseId, attachmentId, annotationId } = action.payload || {};
        if (caseId && attachmentId) {
          const key = annotationKey(caseId, attachmentId);
          state.annotations[key] = (state.annotations[key] || []).filter(
            (a) => (a.id || a._id) !== annotationId
          );
        }
        state.annotationsStatus = "succeeded";
        state.successMessage = "Annotation deleted";
      })
      .addCase(deleteAnnotation.rejected, (state, action) => {
        state.annotationsStatus = "failed";
        state.annotationsError = action.payload;
      });

    // ========== LIKES ==========

    builder
      .addCase(fetchCaseLikes.pending, (state) => {
        state.likesStatus = "loading";
        state.likesError = null;
      })
      .addCase(fetchCaseLikes.fulfilled, (state, action) => {
        const caseId = action.meta.arg;
        state.likesById[caseId] = action.payload;
        state.likesStatus = "succeeded";
      })
      .addCase(fetchCaseLikes.rejected, (state, action) => {
        state.likesStatus = "failed";
        state.likesError = action.payload;
      });

    builder
      .addCase(toggleCaseLike.pending, (state) => {
        state.likesStatus = "loading";
        state.likesError = null;
      })
      .addCase(toggleCaseLike.fulfilled, (state, action) => {
        const caseId = action.meta.arg;
        if (caseId) state.likesById[caseId] = action.payload;
        state.likesStatus = "succeeded";
        state.successMessage = "Like toggled";
      })
      .addCase(toggleCaseLike.rejected, (state, action) => {
        state.likesStatus = "failed";
        state.likesError = action.payload;
      });

    // ========== COMMENTS ==========

    builder
      .addCase(fetchCaseComments.pending, (state) => {
        state.commentsStatus = "loading";
        state.commentsError = null;
      })
      .addCase(fetchCaseComments.fulfilled, (state, action) => {
        const caseId = action.meta.arg;
        state.commentsByCaseId[caseId] = normalizeList(action.payload);
        state.commentsStatus = "succeeded";
      })
      .addCase(fetchCaseComments.rejected, (state, action) => {
        state.commentsStatus = "failed";
        state.commentsError = action.payload;
      });

    builder
      .addCase(createCaseComment.pending, (state) => {
        state.commentsStatus = "loading";
        state.commentsError = null;
      })
      .addCase(createCaseComment.fulfilled, (state, action) => {
        const caseId = action.meta.arg?.caseId;
        if (caseId) {
          const existing = state.commentsByCaseId[caseId] || [];
          state.commentsByCaseId[caseId] = [...existing, action.payload];
        }
        state.commentsStatus = "succeeded";
        state.successMessage = "Comment created";
      })
      .addCase(createCaseComment.rejected, (state, action) => {
        state.commentsStatus = "failed";
        state.commentsError = action.payload;
      });

    builder
      .addCase(updateCaseComment.pending, (state) => {
        state.commentsStatus = "loading";
        state.commentsError = null;
      })
      .addCase(updateCaseComment.fulfilled, (state, action) => {
        const caseId = action.meta.arg?.caseId;
        if (caseId && state.commentsByCaseId[caseId]) {
          state.commentsByCaseId[caseId] = upsertById(
            state.commentsByCaseId[caseId],
            action.payload
          );
        }
        state.commentsStatus = "succeeded";
        state.successMessage = "Comment updated";
      })
      .addCase(updateCaseComment.rejected, (state, action) => {
        state.commentsStatus = "failed";
        state.commentsError = action.payload;
      });

    builder
      .addCase(deleteCaseComment.pending, (state) => {
        state.commentsStatus = "loading";
        state.commentsError = null;
      })
      .addCase(deleteCaseComment.fulfilled, (state, action) => {
        const { caseId, commentId } = action.payload;
        if (state.commentsByCaseId[caseId]) {
          state.commentsByCaseId[caseId] = state.commentsByCaseId[
            caseId
          ].filter((c) => (c.id || c._id) !== commentId);
        }
        state.commentsStatus = "succeeded";
        state.successMessage = "Comment deleted";
      })
      .addCase(deleteCaseComment.rejected, (state, action) => {
        state.commentsStatus = "failed";
        state.commentsError = action.payload;
      });

    // ========== LIVES ==========

    builder
      .addCase(createLive.pending, (state) => {
        state.currentLive.status = "loading";
        state.currentLive.error = null;
      })
      .addCase(createLive.fulfilled, (state, action) => {
        state.currentLive.data = action.payload;
        state.currentLive.status = "succeeded";
        const liveId = action.payload?.id || action.payload?._id;
        if (liveId) state.livesById[liveId] = action.payload;
        state.successMessage = "Live created successfully";
      })
      .addCase(createLive.rejected, (state, action) => {
        state.currentLive.status = "failed";
        state.currentLive.error = action.payload;
      });

    builder
      .addCase(fetchMyLives.pending, (state) => {
        state.livesList.my.status = "loading";
        state.livesList.my.error = null;
      })
      .addCase(fetchMyLives.fulfilled, (state, action) => {
        const data = normalizeList(action.payload);
        state.livesList.my.items = data;
        state.livesList.my.total = action.payload?.total || data.length;
        state.livesList.my.status = "succeeded";
        data.forEach((live) => {
          const id = live?.id || live?._id;
          if (id) state.livesById[id] = live;
        });
      })
      .addCase(fetchMyLives.rejected, (state, action) => {
        state.livesList.my.status = "failed";
        state.livesList.my.error = action.payload;
      });

    builder
      .addCase(fetchPublicLives.pending, (state) => {
        state.livesList.public.status = "loading";
        state.livesList.public.error = null;
      })
      .addCase(fetchPublicLives.fulfilled, (state, action) => {
        const data = normalizeList(action.payload);
        state.livesList.public.items = data;
        state.livesList.public.total = action.payload?.total || data.length;
        state.livesList.public.status = "succeeded";
        data.forEach((live) => {
          const id = live?.id || live?._id;
          if (id) state.livesById[id] = live;
        });
      })
      .addCase(fetchPublicLives.rejected, (state, action) => {
        state.livesList.public.status = "failed";
        state.livesList.public.error = action.payload;
      });

    builder
      .addCase(fetchLiveById.pending, (state) => {
        state.currentLive.status = "loading";
        state.currentLive.error = null;
      })
      .addCase(fetchLiveById.fulfilled, (state, action) => {
        state.currentLive.data = action.payload;
        state.currentLive.status = "succeeded";
        const id = action.payload?.id || action.payload?._id;
        if (id) state.livesById[id] = action.payload;
      })
      .addCase(fetchLiveById.rejected, (state, action) => {
        state.currentLive.status = "failed";
        state.currentLive.error = action.payload;
      });

    builder
      .addCase(updateLive.pending, (state) => {
        state.currentLive.status = "loading";
        state.currentLive.error = null;
      })
      .addCase(updateLive.fulfilled, (state, action) => {
        state.currentLive.data = { ...state.currentLive.data, ...action.payload };
        state.currentLive.status = "succeeded";
        const id = action.payload?.id || action.payload?._id;
        if (id) state.livesById[id] = action.payload;
        state.successMessage = "Live updated successfully";
      })
      .addCase(updateLive.rejected, (state, action) => {
        state.currentLive.status = "failed";
        state.currentLive.error = action.payload;
      });

    builder
      .addCase(startLive.pending, (state) => {
        state.currentLive.status = "loading";
        state.currentLive.error = null;
      })
      .addCase(startLive.fulfilled, (state, action) => {
        state.currentLive.data = { ...state.currentLive.data, ...action.payload };
        state.currentLive.status = "succeeded";
        const id = action.payload?.id || action.payload?._id;
        if (id) state.livesById[id] = action.payload;
        state.successMessage = "Live started successfully";
      })
      .addCase(startLive.rejected, (state, action) => {
        state.currentLive.status = "failed";
        state.currentLive.error = action.payload;
      });

    builder
      .addCase(joinLive.pending, (state) => {
        state.liveConnection.status = "joining";
        state.liveConnection.error = null;
      })
      .addCase(joinLive.fulfilled, (state, action) => {
        state.liveConnection = {
          liveId: action.payload?.liveId || null,
          serverUrl: action.payload?.serverUrl || null,
          participantToken: action.payload?.participantToken || null,
          participant: action.payload?.participant || null,
          status: "ready",
          error: null,
        };
      })
      .addCase(joinLive.rejected, (state, action) => {
        state.liveConnection.status = "failed";
        state.liveConnection.error = action.payload;
      });

    builder
      .addCase(fetchLiveParticipants.pending, (state, action) => {
        const liveId = action.meta.arg?.liveId;
        if (liveId) {
          state.liveParticipantsStatusById[liveId] = "loading";
          state.liveParticipantsErrorById[liveId] = null;
        }
      })
      .addCase(fetchLiveParticipants.fulfilled, (state, action) => {
        const { liveId, payload } = action.payload;
        state.liveParticipantsById[liveId] = normalizeList(payload);
        state.liveParticipantsStatusById[liveId] = "succeeded";
      })
      .addCase(fetchLiveParticipants.rejected, (state, action) => {
        const liveId = action.meta.arg?.liveId;
        if (liveId) {
          state.liveParticipantsStatusById[liveId] = "failed";
          state.liveParticipantsErrorById[liveId] = action.payload;
        }
      });

    builder
      .addCase(leaveLive.pending, (state) => {
        state.liveConnection.status = "leaving";
        state.liveConnection.error = null;
      })
      .addCase(leaveLive.fulfilled, (state) => {
        state.liveConnection = { ...initialState.liveConnection };
        state.liveConnection.status = "disconnected";
      })
      .addCase(leaveLive.rejected, (state, action) => {
        state.liveConnection.status = "failed";
        state.liveConnection.error = action.payload;
      });

    builder
      .addCase(endLive.pending, (state) => {
        state.currentLive.status = "loading";
        state.currentLive.error = null;
      })
      .addCase(endLive.fulfilled, (state, action) => {
        state.currentLive.data = { ...state.currentLive.data, ...action.payload };
        state.currentLive.status = "succeeded";
        state.liveConnection = { ...initialState.liveConnection };
        state.successMessage = "Live ended successfully";
      })
      .addCase(endLive.rejected, (state, action) => {
        state.currentLive.status = "failed";
        state.currentLive.error = action.payload;
      });

    builder
      .addCase(cancelLive.pending, (state) => {
        state.currentLive.status = "loading";
        state.currentLive.error = null;
      })
      .addCase(cancelLive.fulfilled, (state, action) => {
        state.currentLive.data = { ...state.currentLive.data, ...action.payload };
        state.currentLive.status = "succeeded";
        state.liveConnection = { ...initialState.liveConnection };
        state.successMessage = "Live cancelled successfully";
      })
      .addCase(cancelLive.rejected, (state, action) => {
        state.currentLive.status = "failed";
        state.currentLive.error = action.payload;
      });

    builder
      .addCase(fetchLiveComments.pending, (state) => {
        state.liveCommentsStatus = "loading";
        state.liveCommentsError = null;
      })
      .addCase(fetchLiveComments.fulfilled, (state, action) => {
        const liveId = action.meta.arg;
        state.liveCommentsById[liveId] = normalizeList(action.payload);
        state.liveCommentsStatus = "succeeded";
      })
      .addCase(fetchLiveComments.rejected, (state, action) => {
        state.liveCommentsStatus = "failed";
        state.liveCommentsError = action.payload;
      });

    builder
      .addCase(createLiveComment.pending, (state) => {
        state.liveCommentsStatus = "loading";
        state.liveCommentsError = null;
      })
      .addCase(createLiveComment.fulfilled, (state, action) => {
        const liveId = action.meta.arg?.liveId;
        if (liveId) {
          const existing = state.liveCommentsById[liveId] || [];
          state.liveCommentsById[liveId] = [...existing, action.payload];
        }
        state.liveCommentsStatus = "succeeded";
        state.successMessage = "Live comment created";
      })
      .addCase(createLiveComment.rejected, (state, action) => {
        state.liveCommentsStatus = "failed";
        state.liveCommentsError = action.payload;
      });

    builder
      .addCase(updateLiveComment.pending, (state) => {
        state.liveCommentsStatus = "loading";
        state.liveCommentsError = null;
      })
      .addCase(updateLiveComment.fulfilled, (state, action) => {
        const liveId = action.meta.arg?.liveId;
        if (liveId && state.liveCommentsById[liveId]) {
          state.liveCommentsById[liveId] = upsertById(
            state.liveCommentsById[liveId],
            action.payload
          );
        }
        state.liveCommentsStatus = "succeeded";
        state.successMessage = "Live comment updated";
      })
      .addCase(updateLiveComment.rejected, (state, action) => {
        state.liveCommentsStatus = "failed";
        state.liveCommentsError = action.payload;
      });

    builder
      .addCase(deleteLiveComment.pending, (state) => {
        state.liveCommentsStatus = "loading";
        state.liveCommentsError = null;
      })
      .addCase(deleteLiveComment.fulfilled, (state, action) => {
        const { liveId, commentId } = action.payload;
        if (liveId && state.liveCommentsById[liveId]) {
          state.liveCommentsById[liveId] = state.liveCommentsById[
            liveId
          ].filter((comment) => (comment.id || comment._id) !== commentId);
        }
        state.liveCommentsStatus = "succeeded";
        state.successMessage = "Live comment deleted";
      })
      .addCase(deleteLiveComment.rejected, (state, action) => {
        state.liveCommentsStatus = "failed";
        state.liveCommentsError = action.payload;
      });
  },
});

export const {
  clearError,
  clearSuccessMessage,
  setLiveConnectionStatus,
  clearLiveConnection,
  resetState,
} =
  medicalCasesSlice.actions;
export default medicalCasesSlice.reducer;
